import { useState, useEffect, useRef, useCallback } from 'react';
import styles from './CanConfigEditor.module.scss';

function CanConfigEditor({ configData, onConfigChange, isEditingJson }) {
  const [speed, setSpeed] = useState(250);
  const [protocol, setProtocol] = useState('canopen');
  const [mode, setMode] = useState('normal');
  const [power, setPower] = useState(true);
  const [powerAutoCtrl, setPowerAutoCtrl] = useState(true);
  const [terminator120ohm, setTerminator120ohm] = useState(true);
  const [servers, setServers] = useState([
    {
      type: 'tcpserver',
      server_port: 3333,
      target_ip: null,
      target_port: null,
      retry_interval: null,
    },
  ]);

  const lastConfigDataRef = useRef('');
  const isUpdatingFromGuiRef = useRef(false);

  // Speed options
  const speedOptions = [
    { value: 50, label: '50 Kbps' },
    { value: 100, label: '100 Kbps' },
    { value: 125, label: '125 Kbps' },
    { value: 250, label: '250 Kbps' },
    { value: 500, label: '500 Kbps' },
    { value: 1000, label: '1000 Kbps' },
  ];

  // Generate JSON from GUI settings
  const generateConfig = useCallback(() => {
    const config = {
      speed,
      protocol,
      mode,
      power,
      power_auto_ctrl: powerAutoCtrl,
      terminator_120ohm: terminator120ohm,
      servers: servers.map(server => ({
        type: server.type,
        server_port: parseInt(server.server_port) || 3333,
        target_ip: null,
        target_port: null,
        retry_interval: null,
      })),
    };

    return JSON.stringify(config, null, 2);
  }, [speed, protocol, mode, power, powerAutoCtrl, terminator120ohm, servers]);

  // Parse JSON to GUI when configData changes from external source
  useEffect(() => {
    if (
      configData &&
      configData !== lastConfigDataRef.current &&
      !isUpdatingFromGuiRef.current &&
      !isEditingJson
    ) {
      lastConfigDataRef.current = configData;

      try {
        const config = JSON.parse(configData);

        setSpeed(config.speed || 250);
        setProtocol(config.protocol || 'canopen');
        setMode(config.mode || 'normal');
        setPower(config.power !== undefined ? config.power : true);
        setPowerAutoCtrl(config.power_auto_ctrl !== undefined ? config.power_auto_ctrl : true);
        setTerminator120ohm(
          config.terminator_120ohm !== undefined ? config.terminator_120ohm : true
        );

        if (config.servers && Array.isArray(config.servers)) {
          setServers(
            config.servers.map(server => ({
              type: server.type || 'tcpserver',
              server_port: server.server_port || 3333,
              target_ip: null,
              target_port: null,
              retry_interval: null,
            }))
          );
        } else {
          setServers([
            {
              type: 'tcpserver',
              server_port: 3333,
              target_ip: null,
              target_port: null,
              retry_interval: null,
            },
          ]);
        }
      } catch (error) {
        console.error('Error parsing CAN config:', error);
      }
    }
  }, [configData, isEditingJson]);

  // Update parent component when GUI settings change
  useEffect(() => {
    // Skip the first render and when we're parsing from external config
    if (lastConfigDataRef.current) {
      isUpdatingFromGuiRef.current = true;
      const newConfig = generateConfig();
      lastConfigDataRef.current = newConfig;
      onConfigChange(newConfig);

      // Reset the flag after a short delay to allow the parent to update
      setTimeout(() => {
        isUpdatingFromGuiRef.current = false;
      }, 10);
    }
  }, [
    speed,
    protocol,
    mode,
    power,
    powerAutoCtrl,
    terminator120ohm,
    servers,
    generateConfig,
    onConfigChange,
  ]);

  const addServer = () => {
    setServers([
      ...servers,
      {
        type: 'tcpserver',
        server_port: 3333,
        target_ip: null,
        target_port: null,
        retry_interval: null,
      },
    ]);
  };

  const removeServer = index => {
    const newServers = servers.filter((_, i) => i !== index);
    setServers(newServers);
  };

  const updateServer = (index, field, value) => {
    const newServers = [...servers];
    newServers[index] = {
      ...newServers[index],
      [field]: value,
    };
    setServers(newServers);
  };

  const handleServerPortChange = (index, value) => {
    // Only allow positive integers
    if (value === '' || /^\d+$/.test(value)) {
      updateServer(index, 'server_port', value);
    }
  };

  return (
    <div className={styles.container}>
      <h3 className={styles.title}>CAN Bus Settings</h3>

      {/* Speed setting */}
      <div className={styles.setting}>
        <label className={styles.field}>
          <span className={styles.inputLabel}>Speed</span>
          <select
            value={speed}
            onChange={e => setSpeed(parseInt(e.target.value))}
            className={styles.select}
          >
            {speedOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* Protocol setting */}
      <div className={styles.setting}>
        <div className={styles.label}>Protocol</div>
        <div className={styles.radioGroup}>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              value="canopen"
              checked={protocol === 'canopen'}
              onChange={e => setProtocol(e.target.value)}
            />
            CANopen
          </label>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              value="j1939"
              checked={protocol === 'j1939'}
              onChange={e => setProtocol(e.target.value)}
            />
            J1939
          </label>
        </div>
      </div>

      {/* Mode setting */}
      <div className={styles.setting}>
        <div className={styles.label}>Mode</div>
        <div className={styles.radioGroup}>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              value="normal"
              checked={mode === 'normal'}
              onChange={e => setMode(e.target.value)}
            />
            Normal
          </label>
          <label className={styles.radioLabel}>
            <input
              type="radio"
              value="silent"
              checked={mode === 'silent'}
              onChange={e => setMode(e.target.value)}
            />
            Silent
          </label>
        </div>
      </div>

      {/* Power Output setting */}
      <div className={styles.setting}>
        <label className={styles.label}>
          <input type="checkbox" checked={power} onChange={e => setPower(e.target.checked)} />
          Power Output (12V/24V)
        </label>

        {/* Power Auto Control setting - only meaningful when power output is enabled */}
        {power && (
          <label className={`${styles.checkboxLabel} ${styles.subOption}`}>
            <input
              type="checkbox"
              checked={powerAutoCtrl}
              onChange={e => setPowerAutoCtrl(e.target.checked)}
            />
            Power Auto Control
          </label>
        )}
      </div>

      {/* Terminator setting */}
      <div className={styles.setting}>
        <label className={styles.label}>
          <input
            type="checkbox"
            checked={terminator120ohm}
            onChange={e => setTerminator120ohm(e.target.checked)}
          />
          Terminator (120Ω)
        </label>
      </div>

      {/* Servers setting */}
      <div className={`${styles.setting} ${styles.settingFull}`}>
        <div className={styles.listHeader}>
          <div className={styles.label}>Servers</div>
          <button type="button" onClick={addServer} className={styles.addButton}>
            + Add Server
          </button>
        </div>

        <div className={styles.serverList}>
          {servers.map((server, index) => (
            <div key={index} className={styles.serverRow}>
              <div className={styles.fieldGrid}>
                <label className={styles.field}>
                  <span className={styles.inputLabel}>Type</span>
                  <select
                    value={server.type}
                    onChange={e => updateServer(index, 'type', e.target.value)}
                    className={styles.select}
                  >
                    <option value="tcpserver">tcpserver</option>
                    <option value="bluetooth">bluetooth</option>
                  </select>
                </label>
                <label className={styles.field}>
                  <span className={styles.inputLabel}>Server Port</span>
                  <input
                    type="text"
                    value={server.server_port}
                    onChange={e => handleServerPortChange(index, e.target.value)}
                    className={styles.input}
                    placeholder="3333"
                  />
                </label>
              </div>
              <button
                type="button"
                onClick={() => removeServer(index)}
                className={styles.removeButton}
                aria-label={`Remove server ${index + 1}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default CanConfigEditor;
